import { FunctionComponent, useMemo } from "react";

import { Checkbox } from "@/components/Checkbox/Checkbox";
import { classNames } from "@/utils/classNames";

import { CaseStudy } from "../schema/caseStudies";
import { ContentStatus, SegmentStatusFilter } from "../schema/knowledgeDb";
import { getOverallStatusFromList } from "../utils/transformers";

interface CaseStudySidebarProps {
	caseStudies: CaseStudy[];
	activeCaseStudyId: number | null;
	statusFilter: SegmentStatusFilter;
	selectedCaseStudyIds: Set<number>;
	onCaseStudySelect: (caseStudyId: number) => void;
	onAddCaseStudy: () => void;
	onStatusFilterChange: (filter: SegmentStatusFilter) => void;
	onToggleCaseStudySelection: (caseStudyId: number) => void;
	onSelectAll: (caseStudyIds: number[]) => void;
	onBulkApprove: () => void;
}

interface ClientEntry {
	clientName: string;
	count: number;
	firstCaseStudyId: number;
	caseStudyIds: number[];
	overallStatus: ContentStatus | null;
}

export const CaseStudySidebar: FunctionComponent<CaseStudySidebarProps> = ({
	caseStudies,
	activeCaseStudyId,
	statusFilter,
	selectedCaseStudyIds,
	onCaseStudySelect,
	onAddCaseStudy,
	onStatusFilterChange,
	onToggleCaseStudySelection,
	onSelectAll,
	onBulkApprove,
}) => {
	const clientEntries: ClientEntry[] = useMemo(() => {
		const map = new Map<
			string,
			{ count: number; firstId: number; ids: number[]; statuses: ContentStatus[] }
		>();

		for (const cs of caseStudies) {
			const existing = map.get(cs.clientName);

			if (existing) {
				existing.count += 1;
				existing.ids.push(cs.id);
				existing.statuses.push(cs.status);
			} else {
				map.set(cs.clientName, { count: 1, firstId: cs.id, ids: [cs.id], statuses: [cs.status] });
			}
		}

		return Array.from(map.entries()).map(([clientName, { count, firstId, ids, statuses }]) => ({
			clientName,
			count,
			firstCaseStudyId: firstId,
			caseStudyIds: ids,
			overallStatus: getOverallStatusFromList(statuses),
		}));
	}, [caseStudies]);

	const filteredEntries = useMemo(() => {
		if (statusFilter === SegmentStatusFilter.ALL) {
			return clientEntries;
		}

		return clientEntries.filter((entry) => {
			if (statusFilter === SegmentStatusFilter.AI_DRAFTED) {
				return entry.overallStatus === ContentStatus.AI_DRAFTED;
			}

			return entry.overallStatus === ContentStatus.HUMAN_APPROVED;
		});
	}, [clientEntries, statusFilter]);

	const activeClientName = useMemo(() => {
		const active = caseStudies.find((cs) => cs.id === activeCaseStudyId);

		return active?.clientName ?? null;
	}, [caseStudies, activeCaseStudyId]);

	const handleToggleClient = (entry: ClientEntry) => {
		for (const id of entry.caseStudyIds) {
			onToggleCaseStudySelection(id);
		}
	};

	const isClientSelected = (entry: ClientEntry) =>
		entry.caseStudyIds.every((id) => selectedCaseStudyIds.has(id));

	const isBulkMode = selectedCaseStudyIds.size > 0;
	const visibleCaseStudyIds = filteredEntries.flatMap((entry) => entry.caseStudyIds);
	const allSelected =
		visibleCaseStudyIds.length > 0 &&
		visibleCaseStudyIds.every((id) => selectedCaseStudyIds.has(id));
	const someSelected = visibleCaseStudyIds.some((id) => selectedCaseStudyIds.has(id));

	return (
		<div className="w-[280px] min-w-[280px] border-r border-gray-200 bg-brand-white flex flex-col sticky top-0 h-full overflow-y-auto">
			{/* Header */}
			<div className="px-4 py-3 border-b border-gray-200">
				<h3 className="text-sm font-semibold text-gray-900">Case Studies</h3>
				<p className="text-xs text-gray-500 mt-1">
					{caseStudies.length} case {caseStudies.length === 1 ? "study" : "studies"}
				</p>
			</div>

			{/* Status Filter */}
			<div className="px-4 py-2 border-b border-gray-200">
				<select
					className="w-full text-xs border border-gray-300 rounded-lg px-2 py-1.5 bg-white focus:outline-none focus:ring-2 focus:ring-brand-light/30 focus:border-brand-light"
					value={statusFilter}
					onChange={(e) => onStatusFilterChange(e.target.value as SegmentStatusFilter)}
				>
					<option value={SegmentStatusFilter.ALL}>All Case Studies</option>
					<option value={SegmentStatusFilter.AI_DRAFTED}>AI-drafted only</option>
					<option value={SegmentStatusFilter.APPROVED}>Approved only</option>
				</select>
			</div>

			{/* Select all */}
			{visibleCaseStudyIds.length > 0 && (
				<div className="px-4 py-2 border-b border-gray-200">
					<Checkbox
						isChecked={allSelected}
						isIndeterminate={someSelected && !allSelected}
						onChange={() => onSelectAll(allSelected ? [] : visibleCaseStudyIds)}
					>
						<span className="text-xs font-medium text-gray-600">
							Select all ({visibleCaseStudyIds.length})
						</span>
					</Checkbox>
				</div>
			)}

			{/* Client list */}
			<div className="flex-1 overflow-y-auto py-2">
				{filteredEntries.map((entry) => (
					<div
						key={entry.clientName}
						className={classNames(
							"flex items-center w-full px-4 py-2.5 text-left text-sm transition-colors",
							activeClientName === entry.clientName
								? "bg-brand-light/10 text-brand-light font-medium border-l-2 border-brand-light"
								: "text-gray-700 hover:bg-gray-200/50 border-l-2 border-transparent"
						)}
					>
						{/* Bulk selection checkbox */}
						<div className="mr-1 flex-shrink-0" onClick={(e) => e.stopPropagation()}>
							<Checkbox
								isChecked={isClientSelected(entry)}
								onChange={() => handleToggleClient(entry)}
							/>
						</div>
						<button
							className="flex-1 flex items-center justify-between text-left cursor-pointer truncate"
							type="button"
							onClick={() => onCaseStudySelect(entry.firstCaseStudyId)}
						>
							<span className="truncate mr-2">{entry.clientName}</span>
							<div className="flex items-center gap-1 flex-shrink-0">
								{entry.count > 1 && (
									<span className="inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 text-[10px] font-semibold rounded-full bg-gray-200 text-gray-600">
										{entry.count}
									</span>
								)}
								{entry.overallStatus === ContentStatus.AI_DRAFTED && (
									<span className="text-[10px]">✨</span>
								)}
								{entry.overallStatus === ContentStatus.HUMAN_APPROVED && (
									<span className="text-[10px] text-status-success">✓</span>
								)}
							</div>
						</button>
					</div>
				))}
			</div>

			{/* Bulk approve action bar */}
			{isBulkMode && (
				<div className="sticky bottom-0 px-4 py-3 border-t border-gray-200 bg-white">
					<button
						className="w-full px-3 py-2 text-xs font-medium text-white bg-status-warning rounded-lg hover:bg-status-warning/90 transition-colors"
						type="button"
						onClick={onBulkApprove}
					>
						✓ Approve Selected ({selectedCaseStudyIds.size})
					</button>
				</div>
			)}

			{/* Add button */}
			<div className="px-4 py-3 border-t border-gray-200">
				<button
					className="flex items-center justify-center w-full px-3 py-2 text-sm font-medium text-brand-light bg-brand-light/10 rounded-lg hover:bg-brand-light/20 transition-colors cursor-pointer"
					type="button"
					onClick={onAddCaseStudy}
				>
					+ Add Case Study
				</button>
			</div>
		</div>
	);
};
